<template>
  <AppHeaderBack>
    Manage Subscription
  </AppHeaderBack>
  <div class="bg-gray-100 pt-8 pb-8 px-4">
    <div class="max-w-7xl mx-auto space-y-10">
      <!-- Header with Back to Profile Button -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div class="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-3">
              <span class="material-symbols-outlined text-xl text-primary">workspace_premium</span>
              <h1 class="text-2xl font-bold text-gray-800">Subscription Management</h1>
            </div>
          </div>
          <router-link 
            to="/profile"
            class="inline-flex items-center px-4 py-2 border border-primary text-sm font-medium rounded-lg shadow-sm text-primary bg-white hover:bg-primary-lighter transition-colors"
          >
            <span class="material-symbols-outlined text-sm mr-2">person</span>
            Back to Profile
          </router-link>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="space-y-6">
        <div class="animate-pulse flex flex-col items-center">
          <div class="h-8 w-8 bg-primary-light rounded-full mb-4"></div>
          <div class="h-4 bg-primary-light rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-primary-light rounded w-1/2"></div>
        </div>
      </div>

      <!-- Main Content -->
      <template v-else>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Current Subscription Overview Card -->
          <CurrentSubscriptionOverview :profile="profile" />

          <!-- Subscription Actions Card -->
          <SubscriptionActions 
            :profile="profile" 
            :loading="loading"
            @cancel-subscription="showCancelModal = true"
            @contact-support="openContactMail"
          />

          <!-- Payment History Card -->
          <PaymentHistoryCard 
            :payments="recentPayments"
            :loading="loadingPayments"
          />
        </div>
      </template>
    </div>
  </div>

  <!-- Cancel Confirmation Modal -->
  <CancelSubscriptionModal
    :is-visible="showCancelModal"
    :profile="profile"
    :loading="cancelLoading"
    @close="showCancelModal = false"
    @confirm="cancelSubscription"
  />
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { useSubscription } from '@/composables/useSubscription';
import { usePayments } from '@/composables/usePayments';
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';
import CurrentSubscriptionOverview from '@/components/subscription/CurrentSubscriptionOverview.vue';
import SubscriptionActions from '@/components/subscription/SubscriptionActions.vue';
import PaymentHistoryCard from '@/components/subscription/PaymentHistoryCard.vue';
import CancelSubscriptionModal from '@/components/subscription/CancelSubscriptionModal.vue';

export default {
  name: 'UserSubscriptionManagement',
  components: { 
    AppHeaderBack,
    CurrentSubscriptionOverview,
    SubscriptionActions,
    PaymentHistoryCard,
    CancelSubscriptionModal
  },
  data() {
    return {
      showCancelModal: false,
      cancelLoading: false,
      resumeLoading: false,
      recentPayments: [],
      loadingPayments: false
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'loading'])
  },
  methods: {
    ...mapActions(['showAlert', 'fetchUserProfile']),

    async cancelSubscription() {
      if (!this.profile?.plan?.subscriptionId) {
        this.showAlert({
          message: 'No active subscription found to cancel.',
          type: 'error'
        });
        return;
      }
      
      this.cancelLoading = true;
      
      try {
        const { cancelSubscription } = useSubscription();
        await cancelSubscription(this.profile.plan.subscriptionId, this.user?.uid);
        
        this.showAlert({
          message: 'Subscription cancelled successfully. You have been downgraded to the Free plan.',
          type: 'success'
        });
        
        this.showCancelModal = false;

        // Add delay before fetching updated profile
        await new Promise(resolve => setTimeout(resolve, 2088));
        await this.fetchUserProfile();
        
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        this.showAlert({
          message: error.message || 'Failed to cancel subscription. Please try again or contact support.',
          type: 'error'
        });
      } finally {
        this.cancelLoading = false;
      }
    },

    async resumeSubscription() {
      if (!this.profile?.plan?.subscriptionId) {
        this.showAlert({
          message: 'No subscription found to resume.',
          type: 'error'
        });
        return;
      }
      
      this.resumeLoading = true;
      
      try {
        const { resumeSubscription } = useSubscription();
        await resumeSubscription(this.profile.plan.subscriptionId, this.user?.uid);
        
        this.showAlert({
          message: 'Subscription resumed successfully. Your subscription will continue beyond the current period.',
          type: 'success'
        });
        
        await this.fetchUserProfile();
        
      } catch (error) {
        console.error('Error resuming subscription:', error);
        this.showAlert({
          message: error.message || 'Failed to resume subscription. Please try again or contact support.',
          type: 'error'
        });
      } finally {
        this.resumeLoading = false;
      }
    },

    async fetchRecentPayments() {
      this.loadingPayments = true;
      try {
        const { getUserPayments } = usePayments();
        const payments = await getUserPayments(this.user?.uid);
        this.recentPayments = payments.slice(0, 3); // Show only 3 recent payments
      } catch (err) {
        console.error('Error fetching payments:', err);
        this.recentPayments = [];
      } finally {
        this.loadingPayments = false;
      }
    },

    getContactMailto() {
      let subject = encodeURIComponent('Trendztube Support Request');
      let body = '';
      if (this.user) {
        body =
          `Hi Trendztube Support,%0D%0A` +
          `I'm ${this.user.email},%0D%0A` +
          `My plan: ${this.profile?.plan?.name || 'Unknown'}%0D%0A` +
          `Please assist me with ...%0D%0A`;
      } else {
        body =
          `Hi Trendztube,%0D%0A` +
          `I'm Tyrese, I would like assistance with ...%0D%0A`;
      }
      return `mailto:tyteapps@gmail.com?subject=${subject}&body=${body}`;
    },
    openContactMail() {
      window.open(this.getContactMailto(), '_blank');
    }
  },

  async mounted() {
    // Fetch recent payments when component mounts
    if (this.user?.uid) {
      await this.fetchRecentPayments();
    }
  }
};
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
